/**
 * LandingV2 — `GardEn Landing v2.dc.html`, implemented.
 *
 * A pixel-faithful port of the Claude Design file. Every colour, size, spacing,
 * radius, shadow, easing and string below is transcribed from that document; the
 * only translations are structural, from its `<x-dc>` template dialect into
 * React:
 *
 *   <sc-if value="{{ x }}">            → `{x && ( … )}`
 *   {{ binding }}                      → the value from the component's state
 *   style-hover="…"                    → `useHover`-driven style merge (below)
 *   ref="{{ stepRefN }}"               → `stepRefs[N]` from `useLandingMotion`
 *   <x-import HeroBuildAnimation/>     → `<HeroBuildStage/>` (the ported engine)
 *
 * WHY INLINE STYLES: the design authors every rule as an inline `style`
 * attribute, and matching it exactly is the job. React sets these through the
 * CSSOM rather than emitting a `style=` attribute the HTML parser must accept,
 * so the app's strict CSP (`style-src 'self'` with no `unsafe-inline`) is
 * unaffected — no policy change was needed to ship this.
 *
 * WHY THIS DOES NOT USE `--g-*`: the landing is a separate visual world with its
 * own palette (#F7F2E9 / #16453F / #C75A34) and its own two faces. Mapping it
 * onto the product's tokens would change the pixels, which is the one thing this
 * task rules out. The product surfaces behind the login are untouched.
 *
 * KNOWN GAP — the source design is desktop-only. Its root carries
 * `min-width:1100px` and there is not a single breakpoint in the file, so below
 * ~1100px it pans horizontally rather than reflowing. That is reproduced
 * faithfully here rather than papered over with invented breakpoints; a mobile
 * composition is a design decision, not an implementation one.
 *
 * AMENDED 2026-08-25: the design also carried `overflow-x: clip`, and that part
 * is NOT reproduced. Clipping made the off-screen two thirds unreachable on a
 * phone — no scrollbar, no drag, just cut off — which is a defect rather than a
 * faithful port. The root is now split: an outer element scrolls, the inner one
 * keeps the 1100px composition byte-identical. Desktop is unchanged; mobile
 * pans. Still a stopgap, and the sentence above still stands — reflowing is a
 * design decision and nothing here invents one.
 */
import { useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LANDING_NS, registerLandingI18n } from "@/features/landing/landing-i18n";
import { HeroBuildStage } from "@/features/landing/hero/HeroBuild";
import { useCompositionClock } from "@/features/landing/hero/useCompositionClock";
import { DemoPanel } from "@/features/landing/v2/DemoPanel";
import { useLandingMotion } from "@/features/landing/v2/useLandingMotion";
import { useUiStore } from "@/lib/ui-store";

import "@/features/landing/v2/landing-v2.css";

/* ── style-hover ───────────────────────────────────────────────────────────
 * The design dialect's `style-hover="…"` merges a second style object while
 * hovered. Reproduced as a tiny hook so each call site reads like the source.
 * Focus is included as well as hover: the source has no keyboard story at all,
 * and a link whose only affordance is a hover state is unusable from a keyboard.
 */
function useHover(): [boolean, Record<string, unknown>] {
  const [on, setOn] = useState(false);
  return [
    on,
    {
      onMouseEnter: () => setOn(true),
      onMouseLeave: () => setOn(false),
      onFocus: () => setOn(true),
      onBlur: () => setOn(false),
    },
  ];
}

function HoverLink({
  to,
  href,
  style,
  hoverStyle,
  testId,
  children,
}: {
  to?: string;
  href?: string;
  style: CSSProperties;
  hoverStyle: CSSProperties;
  testId?: string;
  children: ReactNode;
}): ReactElement {
  const [on, bind] = useHover();
  const merged = on ? { ...style, ...hoverStyle } : style;
  if (to !== undefined) {
    return (
      <Link to={to} style={merged} data-testid={testId} {...bind}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} style={merged} data-testid={testId} {...bind}>
      {children}
    </a>
  );
}

function HoverBox({
  style,
  hoverStyle,
  children,
}: {
  style: CSSProperties;
  hoverStyle: CSSProperties;
  children: ReactNode;
}): ReactElement {
  const [on, bind] = useHover();
  return (
    <div style={on ? { ...style, ...hoverStyle } : style} {...bind}>
      {children}
    </div>
  );
}

/* ── shared style fragments (transcribed) ─────────────────────────────────── */

// The world's faces, through its tokens (design-world.css), never by name: a
// face is chosen in one place. Display and text are Onest and mono is IBM Plex
// Mono, in every language (owner, 2026-10-09); both carry the Kyrgyz letters.
// «GardEn» is a logotype and keeps the design's Bricolage.
const DISPLAY = "var(--gd-display)";
const WORDMARK = "var(--gd-wordmark)";
const MONO = "var(--gd-mono)";
const TEXT = "var(--gd-text)";
const SHELL: CSSProperties = { maxWidth: 1280, margin: "0 auto", padding: "0 32px" };
/**
 * Anchor landing offset. The design has a 74px sticky header and no
 * `scroll-margin-top` anywhere, so every in-page nav link (#categories, #how,
 * #sellers, #products, #coverage) scrolls its heading UNDER the header. This
 * changes no pixels in the static composition — it only moves where an anchor
 * comes to rest — so it is a functional fix, not a deviation.
 */
const ANCHOR: CSSProperties = { scrollMarginTop: 96 };
const REVEAL: CSSProperties = {
  opacity: 0,
  transform: "translateY(46px)",
  transition: "opacity 0.9s ease, transform 0.9s cubic-bezier(0.2,0.7,0.2,1)",
};
const EYEBROW: CSSProperties = {
  fontFamily: MONO,
  fontSize: 12,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "#C75A34",
};
/** The 45°-rotated square used as the design's only ornament. */
function Diamond({ size = 9, color = "#C75A34", style }: { size?: number; color?: string; style?: CSSProperties }): ReactElement {
  return <span style={{ width: size, height: size, background: color, transform: "rotate(45deg)", ...style }} />;
}

const BAZAARS = ["Дордой", "Баткен-Ата", "Аламедин", "Орто-Сай", "Мадина"] as const;

// The arrays below carry KEYS, not copy: they live at module scope where `t`
// does not exist yet, so the component resolves them at render.
const CATEGORIES = [
  { img: "cat-tile", k: "tile" },
  { img: "cat-flooring", k: "flooring" },
  { img: "cat-paint", k: "paint" },
  { img: "cat-drymix", k: "drymix" },
  { img: "cat-plumbing", k: "plumbing" },
  { img: "cat-electrical", k: "electrical" },
  { img: "cat-wallpaper", k: "wallpaper" },
  { img: "cat-tools", k: "tools" },
] as const;

// `price` and `stall` are DATA and stay untranslated — сом is the currency, and
// «Нурлан курулуш» / Дордой are the market's own proper nouns. Translating a
// stall name would invent a business that does not exist. Same rule the chat
// prompts apply to product names.
const PRODUCTS = [
  { img: "cat-flooring", k: "p1", price: "1 250 сом", stall: "Баткен-Ата · row 7 · «Нурлан курулуш»" },
  { img: "cat-tile", k: "p2", price: "980 сом", stall: "Дордой · container 22-08 · «Айбек стройка»" },
  { img: "cat-paint", k: "p3", price: "2 400 сом", stall: "Аламедин · pavilion 3 · «Салтанат»" },
  { img: "cat-drymix", k: "p4", price: "620 сом", stall: "Дордой · container 14-32 · «Эмир»" },
] as const;

const STEPS = [
  { n: "01", k: "s1" },
  { n: "02", k: "s2" },
  { n: "03", k: "s3" },
] as const;

/** Where the design's `GardEn Assistant.dc.html` links point in this app. */
const ASSISTANT = "/chat";

export function LandingV2(): ReactElement {
  registerLandingI18n();
  // ALIASED to `tr`. useCompositionClock already destructures a `t` (the
  // animation clock's time value) two lines down, and i18next's `t` would
  // shadow it — silently, since both are functions of one argument.
  const { t: tr } = useTranslation(LANDING_NS);
  // W0.4: the ui-store is the one write path for the language (it also calls
  // i18n.changeLanguage). Writing i18n directly is what let the app header
  // highlight EN while the page was Kyrgyz.
  const language = useUiStore((s) => s.language);
  const setLanguage = useUiStore((s) => s.setLanguage);
  const { stageRef, t } = useCompositionClock();
  const { stepRefs, activeStep } = useLandingMotion();

  const stepCard = (i: number): CSSProperties => ({
    position: "absolute",
    inset: 0,
    opacity: activeStep === i ? 1 : 0,
    transition: "opacity 0.5s ease",
    background: "#12332E",
    border: "1px solid rgba(240,233,218,0.12)",
    borderRadius: 22,
    padding: 30,
    display: "flex",
    flexDirection: "column",
  });
  const checkRow: CSSProperties = {
    background: "rgba(240,233,218,0.06)",
    border: "1px solid rgba(240,233,218,0.1)",
    borderRadius: 14,
    padding: "16px 18px",
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "center",
  };

  return (
    // MOBILE: the page STACKS — it does not pan and it does not clip. The
    // breakpoint lives in landing-v2.css because inline styles cannot carry a
    // media query, and this file authors ~169 of them for pixel fidelity. The
    // shell exists so the stylesheet has something to hold onto; desktop is
    // byte-identical, because every override sits inside `max-width: 1099px`.
    <div className="gv2-shell" style={{ width: "100%" }}>
    <div className="gv2" style={{ minWidth: 1100 }}>
      {/* ── Announcement ─────────────────────────────────────────────── */}
      <div
        style={{
          background: "#16453F",
          color: "#F0E9DA",
          fontSize: 13,
          letterSpacing: "0.02em",
          display: "flex",
          justifyContent: "center",
          gap: 10,
          padding: "9px 32px",
        }}
      >
        <span>{tr("v2.announce")}</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>Русский / Кыргызча / English</span>
      </div>

      {/* ── Header ───────────────────────────────────────────────────── */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 60,
          background: "rgba(247,242,233,0.85)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(23,32,29,0.08)",
        }}
      >
        <div
          style={{
            ...SHELL,
            height: 74,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 32,
          }}
        >
          <a
            href="#top"
            style={{ fontFamily: WORDMARK, fontWeight: 800, fontSize: 27, color: "#16453F", letterSpacing: "-0.01em" }}
          >
            GardEn
          </a>
          <nav style={{ display: "flex", alignItems: "center", gap: 30, fontSize: 15, fontWeight: 500 }}>
            <Link to={ASSISTANT}>{tr("v2.nav.assistant")}</Link>
            <a href="#categories">{tr("v2.nav.shops")}</a>
            <a href="#how">{tr("v2.nav.how")}</a>
            <a href="#sellers">{tr("v2.nav.sellers")}</a>
          </nav>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                display: "flex",
                border: "1px solid rgba(23,32,29,0.15)",
                borderRadius: 999,
                overflow: "hidden",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {/* These were three <span>s with no handler — a picture of a
                  switcher. They are real buttons now: `KG` is the label the
                  design uses, `ky` is the i18n code, and the active state is
                  READ from the ui-store, the same source the app header reads (W0.4). */}
              {([["ru", "RU"], ["ky", "KG"], ["en", "EN"]] as const).map(([code, label]) => {
                const active = language === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLanguage(code)}
                    aria-pressed={active}
                    aria-label={tr("v2.lang.switchTo", { lang: tr(`v2.lang.${code}`) })}
                    data-testid={`landing-lang-${code}`}
                    style={{
                      padding: "6px 11px",
                      border: "none",
                      cursor: active ? "default" : "pointer",
                      font: "inherit",
                      background: active ? "#16453F" : "transparent",
                      color: active ? "#F0E9DA" : "#6E6A5E",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <HoverLink
              to={ASSISTANT}
              style={{ background: "#16453F", color: "#F0E9DA", padding: "11px 22px", borderRadius: 999, fontSize: 14, fontWeight: 600, transition: "transform 0.25s" }}
              hoverStyle={{ background: "#0E2A26", color: "#F0E9DA", transform: "translateY(-2px)" }}
            >{tr("v2.nav.open")}</HoverLink>
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section
        id="top"
        style={{
          position: "relative",
          minHeight: "calc(100vh - 120px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
          <div
            ref={stageRef}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: "max(100vw, 133vh)",
              aspectRatio: "4 / 3",
              transform: "translate(-50%, -50%) scale(1.12)",
              transformOrigin: "50% 50%",
              marginTop: 22,
            }}
          >
            <HeroBuildStage t={t} showChips />
          </div>
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(100deg, rgba(247,242,233,0.96) 0%, rgba(247,242,233,0.87) 40%, rgba(247,242,233,0) 72%)",
            }}
          />
        </div>

        <div
          style={{
            position: "relative",
            zIndex: 1,
            ...SHELL,
            padding: "72px 32px 40px",
            display: "flex",
            alignItems: "center",
            flex: 1,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                ...EYEBROW,
                animation: "grise 0.8s 0.05s cubic-bezier(0.2,0.7,0.2,1) both",
              }}
            >
              <span
                style={{ width: 9, height: 9, background: "#C75A34", display: "inline-block", animation: "gspin 6s linear infinite" }}
              />
              <span>{tr("v2.hero.eyebrow")}</span>
            </div>

            <h1
              className="gv2-hero-title"
              style={{
                fontFamily: DISPLAY,
                fontWeight: 800,
                fontSize: "min(6.2vw, 88px)",
                lineHeight: 0.98,
                letterSpacing: "-0.035em",
                margin: 0,
              }}
            >
              <span style={{ display: "block", overflow: "hidden", paddingBottom: "0.06em" }}>
                <span style={{ display: "inline-block", animation: "grise 0.9s 0.15s cubic-bezier(0.2,0.7,0.2,1) both" }}>{tr("v2.hero.h1a")}</span>
              </span>
              <span style={{ display: "block", overflow: "hidden", paddingBottom: "0.09em" }}>
                <span style={{ display: "inline-block", animation: "grise 0.9s 0.3s cubic-bezier(0.2,0.7,0.2,1) both" }}>
                  {tr("v2.hero.h1b")} <em style={{ fontStyle: "italic", color: "#C75A34" }}>{tr("v2.hero.h1c")}</em>
                </span>
              </span>
            </h1>

            <p
              style={{
                fontSize: 19,
                lineHeight: 1.55,
                color: "#4A473D",
                margin: 0,
                maxWidth: 540,
                textWrap: "pretty",
                animation: "grise 0.9s 0.42s cubic-bezier(0.2,0.7,0.2,1) both",
              }}
            >
              {tr("v2.hero.lede")}
            </p>

            <div
              data-testid="gv2-hero-ctas"
              style={{ display: "flex", gap: 14, alignItems: "center", animation: "grise 0.9s 0.54s cubic-bezier(0.2,0.7,0.2,1) both" }}
            >
              <HoverLink
                to={ASSISTANT}
                style={{ background: "#16453F", color: "#F0E9DA", padding: "17px 32px", borderRadius: 999, fontSize: 16, fontWeight: 600, transition: "transform 0.25s, box-shadow 0.25s" }}
                hoverStyle={{ background: "#0E2A26", color: "#F0E9DA", transform: "translateY(-3px)", boxShadow: "0 16px 30px -14px rgba(14,42,38,0.5)" }}
              >{tr("v2.hero.ctaPrimary")}</HoverLink>
              <HoverLink
                href="#how"
                style={{ border: "1px solid rgba(23,32,29,0.2)", background: "#F7F2E9", padding: "16px 30px", borderRadius: 999, fontSize: 16, fontWeight: 600, color: "#17201D", transition: "transform 0.25s" }}
                hoverStyle={{ borderColor: "#16453F", color: "#16453F", transform: "translateY(-3px)" }}
              >{tr("v2.hero.ctaSecondary")}</HoverLink>
            </div>

            <div
              style={{ display: "flex", gap: 22, fontSize: 13.5, color: "#6E6A5E", alignItems: "center", animation: "grise 0.9s 0.66s cubic-bezier(0.2,0.7,0.2,1) both" }}
            >
              <span>
                <strong style={{ color: "#17201D" }}>140+</strong> {tr("v2.hero.statSellers")}
              </span>
              <Diamond size={5} color="#C9C2AE" />
              <span>
                <strong style={{ color: "#17201D" }}>12 400</strong> {tr("v2.hero.statItems")}
              </span>
              <Diamond size={5} color="#C9C2AE" />
              <span>RU / KG / EN</span>
            </div>
          </div>
        </div>

        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            padding: "0 0 26px",
            animation: "grise 1s 1s both",
          }}
        >
          <span
            style={{
              fontFamily: MONO,
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#8B8474",
              background: "rgba(247,242,233,0.85)",
              borderRadius: 999,
              padding: "4px 12px",
            }}
          >{tr("v2.hero.scroll")}</span>
          <span style={{ width: 9, height: 9, background: "#C75A34", animation: "gfloaty 2.2s ease-in-out infinite", transform: "rotate(45deg)" }} />
        </div>
      </section>

      {/* ── Trust row ────────────────────────────────────────────────── */}
      <section
        style={{
          borderTop: "1px solid rgba(23,32,29,0.08)",
          borderBottom: "1px solid rgba(23,32,29,0.08)",
          background: "#F7F2E9",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          data-reveal="1"
          style={{ ...SHELL, padding: "36px 32px", display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 48, ...REVEAL }}
        >
          {[
            [tr("v2.trust.sellersH"), tr("v2.trust.sellersP")],
            [tr("v2.trust.priceH"), tr("v2.trust.priceP")],
            [tr("v2.trust.langH"), tr("v2.trust.langP")],
          ].map(([title, body]) => (
            <div key={title} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              <Diamond size={10} style={{ marginTop: 6, flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 15.5 }}>{title}</div>
                <div style={{ fontSize: 14, color: "#6E6A5E", marginTop: 3 }}>{body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Bazaar marquee ───────────────────────────────────────────── */}
      <div style={{ overflow: "hidden", padding: "30px 0", borderBottom: "1px solid rgba(23,32,29,0.08)" }}>
        <div style={{ display: "flex", gap: 0, width: "max-content", animation: "gmarquee 30s linear infinite" }}>
          {[0, 1].map((copy) => (
            <div
              key={copy}
              aria-hidden={copy === 1}
              style={{
                display: "flex",
                gap: 56,
                alignItems: "center",
                paddingRight: 56,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 26,
                color: "#A89F87",
                whiteSpace: "nowrap",
              }}
            >
              {BAZAARS.map((b) => (
                <span key={b} style={{ display: "contents" }}>
                  <span>{b}</span>
                  <Diamond size={9} color="#E08A63" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Live demo ────────────────────────────────────────────────── */}
      <section id="demo" style={{ ...ANCHOR, padding: "110px 0 0" }}>
        <div
          data-reveal="1"
          style={{ ...SHELL, display: "grid", gridTemplateColumns: "0.95fr 1.05fr", gap: 72, alignItems: "center", ...REVEAL }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <span style={EYEBROW}>{tr("v2.demo.tag")}</span>
            <h2 style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 46, letterSpacing: "-0.02em", lineHeight: 1.04, margin: 0, textWrap: "balance" }}>{tr("v2.demo.h")}</h2>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: "#4A473D", margin: 0, maxWidth: 460, textWrap: "pretty" }}>
              Watch it answer a real question: it checks the quantity, names the fair price, and points to the stall that
              has it — or says plainly when it&apos;s only giving general advice.
            </p>
            <HoverLink
              to={ASSISTANT}
              style={{ alignSelf: "flex-start", background: "#16453F", color: "#F0E9DA", padding: "15px 28px", borderRadius: 999, fontSize: 15, fontWeight: 600, transition: "transform 0.25s" }}
              hoverStyle={{ background: "#0E2A26", color: "#F0E9DA", transform: "translateY(-3px)" }}
            >{tr("v2.demo.cta")}</HoverLink>
          </div>
          <DemoPanel />
        </div>
      </section>

      {/* ── Brand statement ──────────────────────────────────────────── */}
      <section style={{ padding: "150px 0 130px", position: "relative", overflow: "hidden" }}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cpath d='M70 16 124 70 70 124 16 70Z' fill='none' stroke='%2316453F' stroke-opacity='0.06'/%3E%3C/svg%3E\")",
          }}
        />
        <div
          data-reveal="1"
          style={{ position: "relative", maxWidth: 1000, margin: "0 auto", padding: "0 32px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 26, ...REVEAL }}
        >
          <Diamond size={13} />
          <p className="gv2-statement" style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 64, lineHeight: 1.04, letterSpacing: "-0.03em", margin: 0, textWrap: "balance" }}>{tr("v2.statement.a")}<em style={{ fontStyle: "italic", color: "#C75A34" }}>{tr("v2.statement.b")}</em>
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: "#6E6A5E", margin: 0, maxWidth: 560, textWrap: "pretty" }}>
            Nothing at Дордой is listed online. Prices live in the rows and get settled at the container. GardEn puts
            that knowledge in your pocket before you walk in.
          </p>
        </div>
      </section>

      {/* ── Categories ───────────────────────────────────────────────── */}
      <section id="categories" style={{ ...ANCHOR, padding: "0 0 110px" }}>
        <div style={{ ...SHELL, display: "flex", flexDirection: "column", gap: 44 }}>
          <div data-reveal="1" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 32, ...REVEAL }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <span style={EYEBROW}>{tr("v2.nav.shops")}</span>
              <h2 style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 46, letterSpacing: "-0.02em", margin: 0 }}>{tr("v2.shops.h")}</h2>
            </div>
            <Link to={ASSISTANT} style={{ fontSize: 15, fontWeight: 600, borderBottom: "1.5px solid #16453F", paddingBottom: 2 }}>{tr("v2.shops.cta")}</Link>
          </div>
          <div data-reveal="1" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 22, ...REVEAL }}>
            {CATEGORIES.map((c) => (
              <HoverLink
                key={c.img}
                to={ASSISTANT}
                style={{ display: "flex", flexDirection: "column", gap: 12, color: "#17201D", transition: "transform 0.3s" }}
                hoverStyle={{ transform: "translateY(-8px)", color: "#16453F" }}
              >
                <img
                  src={`/landing/${c.img}.png`}
                  alt={tr(`v2.cat.${c.k}`)}
                  style={{ aspectRatio: "1 / 1", borderRadius: 18, width: "100%", objectFit: "cover", display: "block" }}
                />
                <span style={{ fontWeight: 600, fontSize: 16, textAlign: "center" }}>{tr(`v2.cat.${c.k}`)}</span>
              </HoverLink>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────── */}
      <section
        id="how"
        style={{ ...ANCHOR,
          background: "#0E2A26",
          color: "#F0E9DA",
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cpath d='M60 12 108 60 60 108 12 60Z' fill='none' stroke='%23F0E9DA' stroke-opacity='0.04'/%3E%3C/svg%3E\")",
        }}
      >
        <div data-reveal="1" style={{ ...SHELL, padding: "110px 32px 40px", display: "flex", flexDirection: "column", gap: 16, ...REVEAL }}>
          <span style={{ ...EYEBROW, color: "#E08A63" }}>{tr("v2.nav.how")}</span>
          <h2 style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 52, letterSpacing: "-0.025em", lineHeight: 1.02, margin: 0, maxWidth: 820, textWrap: "balance" }}>{tr("v2.how.lede")}</h2>
        </div>

        <div style={{ ...SHELL, padding: "20px 32px 60px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80 }}>
          <div style={{ position: "relative" }}>
            <div style={{ position: "sticky", top: 120, height: 520 }}>
              {/* Step 01 — Upload */}
              <div style={{ ...stepCard(0), gap: 22 }}>
                <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(240,233,218,0.5)" }}>
                  Step 01 · Upload
                </span>
                <div style={{ flex: 1, border: "1.5px dashed rgba(240,233,218,0.3)", borderRadius: 16, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18 }}>
                  <span style={{ width: 14, height: 14, background: "#E08A63", transform: "rotate(45deg)", animation: "gfloaty 3s ease-in-out infinite" }} />
                  <span style={{ fontSize: 15, color: "rgba(240,233,218,0.75)" }}>{tr("v2.how.s1h")}</span>
                  <span style={{ background: "rgba(240,233,218,0.1)", border: "1px solid rgba(240,233,218,0.18)", borderRadius: 999, padding: "8px 16px", fontFamily: MONO, fontSize: 12.5 }}>
                    смета_стр1.jpg · 2.4 MB
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "rgba(240,233,218,0.65)" }}>
                    <span>{tr("v2.how.reading")}</span>
                    <span>70%</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 99, background: "rgba(240,233,218,0.12)" }}>
                    <div style={{ width: "70%", height: "100%", borderRadius: 99, background: "#E08A63" }} />
                  </div>
                </div>
              </div>

              {/* Step 02 — Check */}
              <div style={{ ...stepCard(1), gap: 14 }}>
                <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(240,233,218,0.5)" }}>
                  Step 02 · Check
                </span>
                {[
                  ["Ровнитель · 40 bags", tr("v2.how.wrote"), tr("v2.how.ok1"), "#9FD0AE"],
                  ["Ламинат 33 кл · 65 m²", tr("v2.how.wrote"), tr("v2.how.ok2"), "#9FD0AE"],
                  ["Грунтовка · 2 × 10 L", tr("v2.how.wrote"), tr("v2.how.ok3"), "#9FD0AE"],
                  ["«Электрика, материал»", "lump sum · 48 000 сом", tr("v2.how.warn"), "#F0B27E"],
                ].map(([a, b, c, col]) => (
                  <div key={a} style={checkRow}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 14.5, fontWeight: 600 }}>{a}</span>
                      <span style={{ fontSize: 12.5, color: "rgba(240,233,218,0.55)" }}>{b}</span>
                    </div>
                    <span style={{ fontSize: 13.5, color: col, textAlign: "right" }}>{c}</span>
                  </div>
                ))}
              </div>

              {/* Step 03 — Buy */}
              <div style={{ ...stepCard(2), gap: 14 }}>
                <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(240,233,218,0.5)" }}>
                  Step 03 · Buy
                </span>
                {[
                  // Stall and price are DATA — a bazaar name and сом figure are
                  // the same in every language.
                  [tr("v2.how.m1"), "Дордой · container 14-32", "16 120 сом"],
                  [tr("v2.how.m2"), "Баткен-Ата · row 7", "85 000 сом"],
                  [tr("v2.how.m3"), "Аламедин · pavilion 3", "2 960 сом"],
                ].map(([a, b, c]) => (
                  <div key={a} style={{ ...checkRow, justifyContent: "space-between" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 14.5, fontWeight: 600 }}>{a}</span>
                      <span style={{ fontSize: 12.5, color: "rgba(240,233,218,0.55)" }}>{b}</span>
                    </div>
                    <span style={{ fontWeight: 700, fontSize: 14.5 }}>{c}</span>
                  </div>
                ))}
                <div style={{ marginTop: "auto", background: "#E08A63", color: "#1E120B", borderRadius: 14, padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{tr("v2.how.total")}</span>
                  <span style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 22 }}>104 080 сом</span>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                ref={stepRefs[i]}
                style={{
                  minHeight: "62vh",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 16,
                  opacity: activeStep === i ? 1 : 0.35,
                  transition: "opacity 0.4s ease",
                }}
              >
                <span style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 58, color: "#E08A63", lineHeight: 1 }}>{s.n}</span>
                <h3 style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 30, margin: 0, letterSpacing: "-0.01em" }}>{tr(`v2.how.${s.k}h`)}</h3>
                <p style={{ fontSize: 16.5, lineHeight: 1.6, color: "rgba(240,233,218,0.72)", margin: 0, maxWidth: 440, textWrap: "pretty" }}>{tr(`v2.how.${s.k}p`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured products ────────────────────────────────────────── */}
      <section id="products" style={{ ...ANCHOR, padding: "110px 0" }}>
        <div style={{ ...SHELL, display: "flex", flexDirection: "column", gap: 44 }}>
          <div data-reveal="1" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 32, ...REVEAL }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <span style={EYEBROW}>{tr("v2.products.h")}</span>
              <h2 style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 46, letterSpacing: "-0.02em", margin: 0 }}>
                Stock that answers your estimate
              </h2>
            </div>
            <Link to={ASSISTANT} style={{ fontSize: 15, fontWeight: 600, borderBottom: "1.5px solid #16453F", paddingBottom: 2 }}>
              See all products
            </Link>
          </div>
          <div data-reveal="1" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 22, ...REVEAL }}>
            {PRODUCTS.map((p) => (
              <HoverBox
                key={p.k}
                style={{ background: "#FFFDF8", border: "1px solid rgba(23,32,29,0.1)", borderRadius: 18, overflow: "hidden", display: "flex", flexDirection: "column", transition: "transform 0.3s, box-shadow 0.3s" }}
                hoverStyle={{ transform: "translateY(-8px)", boxShadow: "0 26px 50px -28px rgba(22,69,63,0.4)" }}
              >
                <div style={{ height: 200, position: "relative" }}>
                  <img src={`/landing/${p.img}.png`} alt={tr(`v2.products.${p.k}`)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                  <span style={{ position: "absolute", top: 14, left: 14, background: "#16453F", color: "#F0E9DA", fontSize: 11, fontWeight: 600, padding: "5px 11px", borderRadius: 999 }}>
                    In stock
                  </span>
                </div>
                <div style={{ padding: "18px 20px 20px", display: "flex", flexDirection: "column", gap: 7 }}>
                  <span style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 17.5 }}>{tr(`v2.products.${p.k}`)}</span>
                  <span style={{ fontSize: 13, color: "#6E6A5E" }}>{tr(`v2.products.${p.k}s`)}</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: "#16453F" }}>
                    {p.price} <span style={{ fontSize: 13, fontWeight: 500, color: "#6E6A5E" }}>{tr(`v2.products.${p.k}u`)}</span>
                  </span>
                  <span style={{ fontSize: 12.5, color: "#6E6A5E", borderTop: "1px solid rgba(23,32,29,0.08)", paddingTop: 9, marginTop: 3 }}>{p.stall}</span>
                </div>
              </HoverBox>
            ))}
          </div>
        </div>
      </section>

      {/* ── Coverage ─────────────────────────────────────────────────── */}
      <section id="coverage" style={{ ...ANCHOR, padding: "0 0 110px" }}>
        <div data-reveal="1" style={{ ...SHELL, display: "flex", flexDirection: "column", gap: 44, ...REVEAL }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <span style={EYEBROW}>{tr("v2.coverage.h")}</span>
            <h2 style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 46, letterSpacing: "-0.02em", margin: 0, maxWidth: 720, textWrap: "balance" }}>
              Grounded in the bazaars, not the open web
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 22 }}>
            {[
              // `chips` are the bazaars' own names and stay untranslated; only the
              // prose and the "sellers" noun are keyed.
              { city: "Бишкек", count: `112 ${tr("v2.coverage.sellers")}`, chips: ["Дордой", "Баткен-Ата", "Аламедин", "Орто-Сай", "Мадина"], body: tr("v2.coverage.bishkekP") },
            ].map((c) => (
              <HoverBox
                key={c.city}
                style={{ background: "#FFFDF8", border: "1px solid rgba(23,32,29,0.1)", borderRadius: 22, padding: 34, display: "flex", flexDirection: "column", gap: 18, transition: "transform 0.3s" }}
                hoverStyle={{ transform: "translateY(-6px)" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 30 }}>{c.city}</span>
                  <span style={{ fontSize: 13.5, color: "#6E6A5E" }}>{c.count}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {c.chips.map((ch) => (
                    <span key={ch} style={{ border: "1px solid rgba(23,32,29,0.14)", borderRadius: 999, padding: "8px 16px", fontSize: 14 }}>
                      {ch}
                    </span>
                  ))}
                </div>
                <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: "#6E6A5E", textWrap: "pretty" }}>{c.body}</p>
              </HoverBox>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 22 }}>
            {[
              ["140+", tr("v2.coverage.stat1")],
              ["12 400", tr("v2.coverage.stat2")],
              [tr("v2.coverage.statDaily"), tr("v2.coverage.stat3")],
            ].map(([n, l]) => (
              <div key={n} style={{ borderTop: "2px solid #16453F", paddingTop: 18, display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 38 }}>{n}</span>
                <span style={{ fontSize: 14.5, color: "#6E6A5E" }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── For sellers ──────────────────────────────────────────────── */}
      <section id="sellers" style={{ ...ANCHOR, padding: "0 0 110px" }}>
        <div data-reveal="1" style={{ ...SHELL, ...REVEAL }}>
          <div
            style={{
              background: "#16453F",
              color: "#F0E9DA",
              borderRadius: 28,
              padding: 72,
              display: "grid",
              gridTemplateColumns: "1.2fr 0.8fr",
              gap: 60,
              alignItems: "center",
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cpath d='M60 12 108 60 60 108 12 60Z' fill='none' stroke='%23F0E9DA' stroke-opacity='0.05'/%3E%3C/svg%3E\")",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <span style={{ ...EYEBROW, color: "#E08A63" }}>{tr("v2.nav.sellers")}</span>
              <h2 style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 46, letterSpacing: "-0.02em", lineHeight: 1.05, margin: 0, textWrap: "balance" }}>
                Your container, in every estimate.
              </h2>
              <p style={{ fontSize: 17, lineHeight: 1.6, color: "rgba(240,233,218,0.75)", margin: 0, maxWidth: 480, textWrap: "pretty" }}>
                List your stock once — GardEn matches it to every renovation plan that needs it. Buyers arrive knowing
                the name, the amount and the fair price.
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "flex-start" }}>
              {/*
                * Routes to the SELLER door, not the consumer one.
                *
                * Every link in the design file points at `GardEn Assistant.dc.html`
                * because a design mock has one destination; in the product a seller
                * arriving from the landing must land on `/seller/login`, which is
                * the persona-door contract Track 0 established after users were
                * repeatedly dropped at the consumer door (SCRUM-145). The button is
                * pixel-identical — same label, fill, radius and hover — only its
                * `to` and its test id carry product meaning.
                */}
              <HoverLink
                to="/seller/login"
                testId="landing-seller-dashboard"
                style={{ background: "#F0E9DA", color: "#16453F", padding: "16px 30px", borderRadius: 999, fontSize: 16, fontWeight: 700, transition: "transform 0.25s" }}
                hoverStyle={{ background: "#E08A63", color: "#1E120B", transform: "translateY(-3px)" }}
              >
                List your inventory
              </HoverLink>
              <span style={{ fontSize: 13.5, color: "rgba(240,233,218,0.6)" }}>
                Free while the catalogue grows · updates by phone or app
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Newsletter ───────────────────────────────────────────────── */}
      <section style={{ padding: "0 0 120px" }}>
        <div
          data-reveal="1"
          style={{ maxWidth: 760, margin: "0 auto", padding: "0 32px", display: "flex", flexDirection: "column", alignItems: "center", gap: 18, textAlign: "center", ...REVEAL }}
        >
          <Diamond size={12} />
          <h2 style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 40, letterSpacing: "-0.02em", margin: 0 }}>{tr("v2.news.h")}</h2>
          <p style={{ fontSize: 16.5, lineHeight: 1.6, color: "#6E6A5E", margin: 0, maxWidth: 480, textWrap: "pretty" }}>{tr("v2.news.p")}</p>
          <form
            data-testid="gv2-newsletter"
            style={{ display: "flex", gap: 12, width: "100%", maxWidth: 480, marginTop: 8 }}
            onSubmit={(e) => e.preventDefault()}
          >
            <label htmlFor="gv2-email" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
              Email
            </label>
            <input
              id="gv2-email"
              type="email"
              placeholder="you@email.kg"
              style={{ flex: 1, border: "1px solid rgba(23,32,29,0.2)", borderRadius: 999, padding: "15px 22px", fontSize: 15, background: "#FFFDF8", color: "#17201D", outline: "none", fontFamily: TEXT }}
            />
            <button
              type="submit"
              style={{ background: "#16453F", color: "#F0E9DA", border: "none", borderRadius: 999, padding: "15px 28px", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: TEXT }}
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer style={{ background: "#101C19", color: "#D8D0BA" }}>
        <div style={{ ...SHELL, padding: "72px 32px 0", display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: 56 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <span style={{ fontFamily: WORDMARK, fontWeight: 800, fontSize: 26, color: "#F0E9DA" }}>GardEn</span>
            <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: "rgba(216,208,186,0.65)", maxWidth: 300, textWrap: "pretty" }}>{tr("v2.footer.tagline")}</p>
            <div style={{ display: "flex", gap: 8, fontSize: 12, fontWeight: 600 }}>
              {/* Second dead switcher, same treatment as the header one. */}
              {([["ru", "RU"], ["ky", "KG"], ["en", "EN"]] as const).map(([code, label]) => {
                const active = language === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLanguage(code)}
                    aria-pressed={active}
                    aria-label={tr("v2.lang.switchTo", { lang: tr(`v2.lang.${code}`) })}
                    data-testid={`landing-footer-lang-${code}`}
                    style={{
                      border: "1px solid rgba(216,208,186,0.25)",
                      borderRadius: 999,
                      padding: "5px 12px",
                      font: "inherit",
                      cursor: active ? "default" : "pointer",
                      background: active ? "rgba(240,233,218,0.14)" : "transparent",
                      color: "inherit",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {[
            { head: "Product", items: [["Assistant", ASSISTANT], ["Shops", "#categories"], ["Products", "#products"], ["For sellers", "#sellers"]] },
            { head: "Cities", items: [["Bishkek", "#coverage"]] },
            { head: "Contact", items: [["hello@gardenapp.kg", "#top"], ["+996 555 00 00 00", "#top"]] },
          ].map((col) => (
            <div key={col.head} style={{ display: "flex", flexDirection: "column", gap: 13, fontSize: 14.5 }}>
              <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(216,208,186,0.5)" }}>
                {col.head}
              </span>
              {col.items.map(([label, href]) => (
                <HoverLink
                  key={label}
                  {...(href!.startsWith("#") ? { href } : { to: href })}
                  style={{ color: "#D8D0BA" }}
                  hoverStyle={{ color: "#E08A63" }}
                >
                  {label}
                </HoverLink>
              ))}
              {col.head === "Contact" && (
                <span style={{ color: "rgba(216,208,186,0.65)" }}>{tr("v2.footer.hours")}</span>
              )}
            </div>
          ))}
        </div>
        <div
          style={{ maxWidth: 1280, margin: "56px auto 0", padding: 32, borderTop: "1px solid rgba(216,208,186,0.12)", display: "flex", justifyContent: "space-between", fontSize: 13, color: "rgba(216,208,186,0.5)" }}
        >
          <span>{tr("v2.footer.rights")}</span>
          <span>{tr("v2.footer.where")}</span>
        </div>
      </footer>
    </div>
    </div>
  );
}

export default LandingV2;
